import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function platformUrl(mainDomain, slug) {
  if (!mainDomain || !slug) return '';
  return `https://${mainDomain}/${slug}`;
}

function customUrl(domain, path) {
  if (!domain) return '';
  return `https://${domain}${path ? `/${path}` : ''}`;
}

export default function ChatLinks() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [mainDomain, setMainDomain] = useState('');
  const [cnameTarget, setCnameTarget] = useState('');
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, domain] = await Promise.all([
        api.get('/chat-links'),
        api.get('/chat-links/main-domain')
      ]);
      setRows(list.data.rows || []);
      setMainDomain(domain.data.mainDomain || '');
      setCnameTarget(domain.data.cnameTarget || '');
      const next = {};
      for (const row of list.data.rows || []) {
        next[row.modelId] = {
          platformSlug: row.link?.platformSlug || '',
          customDomain: row.link?.customDomain || '',
          customPath: row.link?.customPath || '',
          displayName: row.link?.displayName || '',
          openerText: row.link?.openerText || '',
          enabled: row.link ? row.link.enabled !== false : true
        };
      }
      setDrafts(next);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not load chat links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function updateDraft(modelId, field, value) {
    setDrafts((prev) => ({ ...prev, [modelId]: { ...prev[modelId], [field]: value } }));
  }

  async function saveDomain(event) {
    event.preventDefault();
    try {
      await api.patch('/chat-links/main-domain', { mainDomain });
      toast.success('Main domain saved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save the domain');
    }
  }

  async function generateMissing() {
    try {
      const res = await api.post('/chat-links/generate');
      toast.success(`Created ${res.data.created || 0} link${res.data.created === 1 ? '' : 's'}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not generate links');
    }
  }

  async function saveRow(modelId) {
    const draft = drafts[modelId] || {};
    try {
      await api.patch(`/chat-links/${modelId}`, draft);
      toast.success('Chat link saved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save this link');
    }
  }

  async function uploadOpener(modelId, file) {
    if (!file) return;
    const form = new FormData();
    form.append('photo', file);
    try {
      await api.post(`/chat-links/${modelId}/opener-photo`, form);
      toast.success('Opener photo saved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not upload the photo');
    }
  }

  async function clearOpener(modelId) {
    try {
      await api.patch(`/chat-links/${modelId}`, { clearOpenerPhoto: true });
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not remove the photo');
    }
  }

  function copy(value) {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => toast.success('Copied')).catch(() => toast.error('Could not copy'));
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Chat links</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Publish a model on the main domain, or hook a domain of its own. Threads show up in Chatbot → Conversations with source chatlink.
        </p>
      </div>

      <form onSubmit={saveDomain} className="card p-4 space-y-3">
        <h2 className="font-medium text-gray-900 dark:text-white">Main domain</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Paths on this host become one link per model, for example chatme.link/amybliss.
          {cnameTarget ? ` Point a CNAME at ${cnameTarget}, then add the hostname on that host so TLS exists.` : ' Point the domain at this app, then add it as a custom domain so TLS exists.'}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={mainDomain}
            onChange={(event) => setMainDomain(event.target.value)}
            disabled={!isAdmin}
            placeholder="chatme.link"
            className="form-input flex-1"
          />
          {isAdmin && <button type="submit" className="btn btn-primary">Save domain</button>}
        </div>
        <button type="button" onClick={generateMissing} className="btn btn-secondary">
          Generate missing links
        </button>
      </form>

      {loading ? (
        <div className="text-sm text-gray-500">Loading models…</div>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const draft = drafts[row.modelId] || {};
            const shared = platformUrl(mainDomain, draft.platformSlug || row.link?.platformSlug);
            const own = customUrl(draft.customDomain, draft.customPath);
            const preview = `${window.location.origin}/c/${draft.platformSlug || row.link?.platformSlug || ''}`;
            return (
              <div key={row.modelId} className="card p-4 space-y-3">
                <div className="flex items-center gap-3">
                  {row.profilePicture ? (
                    <img src={row.profilePicture} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                  )}
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{row.modelName}</div>
                    <label className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={draft.enabled !== false}
                        onChange={(event) => updateDraft(row.modelId, 'enabled', event.target.checked)}
                      />
                      Enabled
                    </label>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    Path on main domain
                    <input
                      value={draft.platformSlug || ''}
                      onChange={(event) => updateDraft(row.modelId, 'platformSlug', event.target.value)}
                      className="form-input mt-1 w-full"
                      placeholder="amybliss"
                    />
                  </label>
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    Shown name
                    <input
                      value={draft.displayName || ''}
                      onChange={(event) => updateDraft(row.modelId, 'displayName', event.target.value)}
                      className="form-input mt-1 w-full"
                      placeholder={row.modelName}
                    />
                  </label>
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    Domain for this model
                    <input
                      value={draft.customDomain || ''}
                      onChange={(event) => updateDraft(row.modelId, 'customDomain', event.target.value)}
                      className="form-input mt-1 w-full"
                      placeholder="hmu.fun"
                    />
                  </label>
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    Path on that domain
                    <input
                      value={draft.customPath || ''}
                      onChange={(event) => updateDraft(row.modelId, 'customPath', event.target.value)}
                      className="form-input mt-1 w-full"
                      placeholder="letshavefun (blank = the root)"
                    />
                  </label>
                </div>

                <label className="block text-sm text-gray-600 dark:text-gray-300">
                  Opener line
                  <textarea
                    value={draft.openerText || ''}
                    onChange={(event) => updateDraft(row.modelId, 'openerText', event.target.value)}
                    className="form-input mt-1 w-full"
                    rows={2}
                    placeholder="Leave blank to use the model’s usual X opener"
                  />
                </label>

                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {row.link?.openerPhotoUrl && (
                    <img src={row.link.openerPhotoUrl} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  )}
                  <label className="btn btn-secondary cursor-pointer">
                    Opener photo
                    <input type="file" accept="image/*" className="hidden" onChange={(event) => uploadOpener(row.modelId, event.target.files?.[0])} />
                  </label>
                  {row.link?.openerPhotoUrl && (
                    <button type="button" className="text-red-500" onClick={() => clearOpener(row.modelId)}>Remove photo</button>
                  )}
                </div>

                <div className="space-y-1 text-sm">
                  {shared && <button type="button" className="block text-blue-500" onClick={() => copy(shared)}>{shared}</button>}
                  {own && <button type="button" className="block text-blue-500" onClick={() => copy(own)}>{own}</button>}
                  {(draft.platformSlug || row.link?.platformSlug) && (
                    <button type="button" className="block text-gray-500" onClick={() => copy(preview)}>Preview {preview}</button>
                  )}
                </div>

                <button type="button" className="btn btn-primary" onClick={() => saveRow(row.modelId)}>Save</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
